import { EventNoteOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { humanizeEventLabel } from 'helpers/fileHistory';
import useTranslate from 'hooks/useTranslate';
import type { ActivityEventItem } from 'types';
import { formatDateTime } from 'utils/dateUtils';

interface EventListProps {
    events: ActivityEventItem[];
    loading?: boolean;
    onSelect: (event: ActivityEventItem) => void;
}

const denseCellSx = { py: 1, px: 1.5 };

const userLabel = (event: ActivityEventItem) => event.user?.name || event.user?.email || '—';

const EventChip = ({ eventType }: { eventType: string }) => (
    <Chip
        size="small"
        label={humanizeEventLabel(eventType)}
        sx={{
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: 1,
            bgcolor: 'rgba(131, 83, 226, 0.10)',
            color: 'primary.main',
            maxWidth: '100%',
            '& .MuiChip-label': { px: 1 },
        }}
    />
);

const ViewEventButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <Tooltip title={label}>
        <IconButton size="small" color="primary" sx={{ p: 0.5 }} onClick={onClick}>
            <VisibilityOutlined sx={{ fontSize: 18 }} />
        </IconButton>
    </Tooltip>
);

export const EventList = ({ events, loading = false, onSelect }: EventListProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (loading) {
        return <TableSkeleton tableColumns={6} />;
    }

    if (!events.length) {
        return (
            <Result
                icon={<EventNoteOutlined sx={{ fontSize: 32 }} />}
                title={translate('noEventsFound')}
                description={translate('noEventsFoundDescription')}
            />
        );
    }

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {events.map((event) => (
                    <Paper
                        key={event.id}
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            minWidth: 0,
                        }}
                    >
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <EventChip eventType={event.event_type} />
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ alignItems: 'center', flexShrink: 0 }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDateTime(event.timestamp, 'MMM D, HH:mm')}
                                    </Typography>
                                    <ViewEventButton
                                        label={translate('eventDetails')}
                                        onClick={() => onSelect(event)}
                                    />
                                </Stack>
                            </Stack>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                            >
                                {userLabel(event)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-word', display: 'block' }}
                            >
                                {event.user?.email || '—'}
                            </Typography>
                            {event.display_message ? (
                                <Typography variant="body2" color="text.secondary">
                                    {event.display_message}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, overflowX: 'auto' }}
        >
            <Table
                size="small"
                sx={{
                    minWidth: 760,
                    '& .MuiTableCell-root': denseCellSx,
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 180 }}>
                            {translate('eventTimestamp')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{translate('eventType')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{translate('eventUser')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{translate('email')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{translate('eventMessage')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 72 }}>
                            {translate('actions')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {events.map((event) => (
                        <TableRow key={event.id} hover>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDateTime(event.timestamp)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <EventChip eventType={event.event_type} />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {userLabel(event)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ wordBreak: 'break-word' }}
                                >
                                    {event.user?.email || '—'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {event.display_message || '—'}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <ViewEventButton
                                    label={translate('eventDetails')}
                                    onClick={() => onSelect(event)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
